<template>
  <div class="bg-light min-vh-100">
    <main class="container">
      <div class="row justify-content-center">
        <div class="col-lg-10">
          <div class="card shadow-sm">
            <div class="card-body">
              <!-- Header section with template name, status, and actions -->
              <div
                class="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2"
              >
                <!-- Template title and subtitle -->
                <div>
                  <h1 class="h4 mb-1">
                    {{ template?.template_name || "Template" }}
                  </h1>
                  <p class="help mb-0">
                    Template details and step ordering.
                  </p>
                </div>

                <!-- Action buttons and status badge -->
                <div class="d-flex align-items-center gap-2">
                  <!-- Status badge: shows Active or Inactive -->
                  <span
                    v-if="template"
                    class="badge"
                    :class="template.active_template ? 'bg-success' : 'bg-secondary'"
                  >
                    {{ template.active_template ? "Active" : "Inactive" }}
                  </span>

                  <button
                    v-if="isSupervisor && template?.active_template"
                    class="btn btn-outline-danger btn-sm"
                    :disabled="loading"
                    @click="disableTemplate"
                  >
                    Disable Template
                  </button>


                  <!-- Edit button: only visible to supervisors and admins -->
                  <button
                    v-if="isSupervisor"
                    class="btn btn-outline-secondary btn-sm"
                    @click="goEdit"
                  >
                    Edit Template
                  </button>

                  <!-- Back button: returns to templates list -->
                  <button
                    class="btn btn-outline-secondary btn-sm"
                    @click="goBack"
                  >
                    Back
                  </button>
                </div>
              </div>

              <!-- Loading spinner: displayed while fetching template data -->
              <div v-if="loading" class="text-center py-5">
                <div class="spinner-border" role="status">
                  <span class="visually-hidden">Loading template...</span>
                </div>
              </div>

              <!-- Main content: only shown when not loading -->
              <div v-else>
                <!-- Error alert: displayed if API call fails -->
                <div v-if="error" class="alert alert-danger py-2 mb-3">
                  {{ error }}
                </div>

                <!-- Template content: shown only if template data exists -->
                <div v-if="template">
                  <!-- Metadata section: displays template info and description -->
                  <div class="row mb-3">
                    <!-- Left column: template metadata -->
                    <div class="col-md-6">
                      <h2 class="h6">Template Info</h2>
                      <dl class="row mb-0">
                        <dt class="col-sm-4">Name</dt>
                        <dd class="col-sm-8">{{ template.template_name }}</dd>

                        <dt class="col-sm-4">ID</dt>
                        <dd class="col-sm-8">{{ template.template_id }}</dd>

                        <dt class="col-sm-4">Version</dt>
                        <dd class="col-sm-8">
                          {{ template.template_version || "—" }}
                        </dd>

                        <dt class="col-sm-4">Status</dt>
                        <dd class="col-sm-8">
                          {{ template.active_template ? "Active" : "Inactive" }}
                        </dd>
                      </dl>
                    </div>

                    <!-- Right column: template description -->
                    <div class="col-md-6">
                      <h2 class="h6">Description</h2>
                      <p class="mb-0 text-muted">
                        {{
                          template.template_description ||
                          "No description provided."
                        }}
                      </p>
                    </div>
                  </div>

                  <!-- Steps section: displays ordered list of template steps -->
                  <div class="mt-4">
                    <!-- Section header with helper text -->
                    <div
                      class="mb-2 d-flex justify-content-between align-items-center"
                    >
                      <h2 class="h6 mb-0">Steps</h2>
                      <span class="help">
                        Steps are shown in execution order.
                      </span>
                    </div>

                    <!-- Empty state: shown when no steps exist -->
                    <div v-if="orderedSteps.length === 0" class="text-muted">
                      No steps associated with this template.
                    </div>

                    <!-- Steps table: displays all steps in order -->
                    <div v-else class="table-responsive">
                      <table class="table table-sm align-middle">
                        <thead class="table-light">
                          <tr>
                            <th style="width: 60px;">#</th>
                            <th>Step Name</th>
                            <th>Description</th>
                          </tr>
                        </thead>
                        <tbody>
                          <!-- Row for each step, ordered by step_position -->
                          <tr
                            v-for="(step, index) in orderedSteps"
                            :key="step.step_id || index"
                          >
                            <td>
                              {{ step.step_position ?? index + 1 }}
                            </td>
                            <td>{{ step.step_name }}</td>
                            <td class="text-muted">
                              {{ step.step_description }}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import { APIService } from "../http/APIService";

export default {
  name: "TemplateDetail",
  data() {
    return {
      template: null, // Holds the template data fetched from API
      loading: false, // Track API loading state
      error: null,    // Store error message if fetch fails
      api: new APIService(), // API service instance for HTTP requests
      currentUser: null, // Stores current authenticated user info
    };
  },
  computed: {
    // Returns template steps sorted by step_position in ascending order
    orderedSteps() {
      if (!this.template) return [];

      // Convert step_details to array if it exists
      const raw = Array.isArray(this.template.step_details)
        ? this.template.step_details
        : [];

      if (!raw.length) return [];

      // Sort by step_position, defaulting to 0 if not set
      return [...raw].sort((a, b) => {
        const ao = a.step_position ?? 0;
        const bo = b.step_position ?? 0;
        return ao - bo;
      });
    },
    // Determines if current user has supervisor or admin privileges
    isSupervisor() {
      const role = (this.currentUser?.role || "").toLowerCase();
      return role === "supervisor" || role === "admin";
    }
  },
  created() {
    // Initialize component: fetch template and load user info
    this.fetchTemplate();
    this.loadAuthFromStorage();
  },
  methods: {

    async disableTemplate() {
  if (!this.template || !this.template.template_id) return;

  this.saving = true;
  this.error = null;

  try {
    const accessToken = localStorage.getItem("accessToken");
    const id = this.template.template_id;

    // Pure deactivation payload – triggers the "deactivate only" branch
    const res = await this.api.updateTemplate(id, { active_template: false }, accessToken);

    // Update local template state with server response
    this.template = res.data;
  } catch (err) {
    console.error("Failed to disable template:", err.response?.data || err);
    this.error = "Unable to disable template.";
  } finally {
    this.saving = false;
  }
},



    // Retrieves current user info from localStorage and parses JSON
    loadAuthFromStorage() {
      const raw = localStorage.getItem("currentUser");
      try {
        this.currentUser = raw ? JSON.parse(raw) : null;
      } catch {
        this.currentUser = null;
      }
    },

    // Fetches template details from API using route parameter ID
    async fetchTemplate() {
      this.loading = true;
      this.error = null;
      try {
        const id = this.$route.params.id;
        const accessToken = localStorage.getItem("accessToken");
        const res = await this.api.getTemplate(id, accessToken);
        this.template = res.data;
      } catch (err) {
        console.error("Failed to load template:", err.response?.data || err);
        this.error = "Unable to load template.";
      } finally {
        this.loading = false;
      }
    },
   
    // Navigates to edit template page with current template ID
    goEdit() {
      if (!this.template) return;
      this.$router.push({
        name: "EditTemplate",
        params: { id: this.template.template_id },
      });
    },

    // Navigates back to the templates list view
    goBack() {
      this.$router.push({ name: "ViewTemplates" });
    },
  },
};
</script>
